"use client";

import { ArrowRight, Globe2, Loader2 } from "lucide-react";
import { useIntl } from "react-intl";
import { usePersonalizedDemoIntentForm } from "./usePersonalizedDemoIntentForm";

/** Shared website form; presentation parents own only the surrounding promise. */
export default function PersonalizedDemoWebsiteForm({
  layout = "inline",
}: {
  layout?: "inline" | "stacked";
}) {
  const intl = useIntl();
  const form = usePersonalizedDemoIntentForm();

  return (
    <>
      <form
        className={`flex flex-col gap-3 ${layout === "inline" ? "sm:flex-row" : ""}`}
        onSubmit={form.submit}
      >
        <label className="relative flex-1">
          <span className="sr-only">
            {intl.formatMessage({
              id: "home.personalizedDemo.websiteLabel",
              defaultMessage: "Your website address",
            })}
          </span>
          <Globe2
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            inputMode="url"
            required
            maxLength={2048}
            autoComplete="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={form.websiteUrl}
            onChange={(event) => form.setWebsiteUrl(event.target.value)}
            onBlur={form.normalizeWebsiteUrl}
            placeholder="https://votre-site.fr"
            className="h-14 w-full rounded-xl border border-slate-200 bg-white pl-12 pr-4 text-base text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
          />
        </label>
        <button
          type="submit"
          disabled={form.isSubmitting}
          className={`inline-flex items-center justify-center gap-2 rounded-xl text-white transition disabled:cursor-wait disabled:opacity-70 ${layout === "stacked" ? "min-h-14 bg-indigo-600 px-3 py-3 text-sm font-semibold hover:bg-indigo-700" : "h-14 bg-gradient-to-r from-indigo-600 to-violet-600 px-6 text-base font-bold shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 hover:shadow-xl"}`}
        >
          {form.isSubmitting ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          )}
          {intl.formatMessage({
            id: "home.personalizedDemo.cta",
            defaultMessage: "Create my free demo",
          })}
        </button>
      </form>
      {form.error && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">
          {form.error}
        </p>
      )}
    </>
  );
}
